<template>
    <div class="w-full h-screen main-background flex-column flex-grow justify-center align-center">
        <div class="flex justify-center m-2"><input v-model="filterTwitchUserID" @input="filterEvents" placeholder="Twitch User Name" /></div>
        <div class="flex justify-center items-center w-full space-x-2">
            <select v-model="filterDayOfWeek" @change="filterByDayOfWeek">
                <option value="">Day of Week</option>
                <option v-for="day in daysOfWeek" :key="day">{{ day }}</option>
            </select>
            <select v-model="filterHourOfDay" @change="filterByHourOfDay">
                <option value="">Hour of Day</option>
                <option v-for="hour in hoursOfDay" :key="hour">{{ hour }}</option>
            </select>
            <button @click="resetFilters" class="bg-white border-b border-2 rounded">Reset</button>
        </div>
        <div class="flex flex-row flex-grow justify-center align-center p-2 space-x-2">
            <Datepicker v-model="selectedStartDate" placeholder="Select a Start date"></Datepicker>
            <Datepicker v-model="selectedEndDate" placeholder="Select a End date"></Datepicker>
            <button @click="filterByDateRange" class="bg-white border-b border-2 rounded">Apply Daterange</button>
        </div>
        <div class="text-xs text-white w-full text-center">Filters can be used in any combination</div>
        <div class="text-xs text-white w-full text-center">All times in browser local timezone</div>
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-message">Loading...</div>
        </div>
        <div class="bg-white border-black border-2 m-2">
            <FullCalendar
                ref="fullCalendar"
                :options="calendarOptions"
            />
        </div>
        <div v-if="showModal" class="modal w-full h-full" @click="closeModal">
            <div class="modal-content" @click.stop>
                <span @click="closeModal" class="close">&times;</span>
                <div class="border-black border-b-2">Active Users for Selected Hour: {{ activeUserNames.length }}</div>
                <ul class="user-list w-full h-full pl-4">
                    <li class="user-list-item" v-for="user in activeUserNames" :key="user.userName">
                        <a :href="`http://twitch.tv/${user.userName}`" class="underline">{{ user.userName }}</a>
                        <br/>Avg Viewers: {{ Math.round(user.avgViewers) }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid';
import Datepicker from 'vuejs-datepicker';


export default {
  components: {
    FullCalendar,
    Datepicker
  },
  data() {
    return {
        heatmapData: null,
        filterTwitchUserID: "",
        showModal: false,
        activeUserNames: [],
        userViewerStats: {},
        calendarOptions: {
            plugins: [ dayGridPlugin, timeGridPlugin, interactionPlugin ],
            initialView: 'dayGridMonth',
            dateClick: this.handleDateClick,
            height: 'auto',
            headerToolbar: {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
            },
            events: [],
            eventTimeFormat: {
                hour: 'numeric',
                meridiem: 'short'
            },
        },
        filterDateRange: null,
        filterDayOfWeek: "",
        filterHourOfDay: "",
        daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        hoursOfDay: Array.from({ length: 24 }, (_, i) => {
            const amOrPm = i < 12 ? 'AM' : 'PM';
            const hour = i % 12 || 12;
            return `${hour} ${amOrPm}`;
        }),
        selectedStartDate: null,
        selectedEndDate: null,
        isLoading: true,
    };
  },
  mounted() {
    this.fetchHeatmapData();
    this.calendarOptions.eventClick = this.handleEventClick;
  },
  methods: {
    async fetchHeatmapData() {
        try {
            const response = await fetch(`${process.env.VUE_APP_NODE_SERVER_BASE_URL}/heatmap`);
            const data = await response.json();
            this.heatmapData = data;
            console.log("Retrieved heatmap data: ", this.heatmapData);
            this.processHeatmapData(); // Call processHeatmapData here
        } catch (error) {
            console.error("An error occurred while fetching heatmap data: ", error);
        }
    },
    processHeatmapData() {
        if (!this.heatmapData) return;

        let processedEvents = {};
        let userViewerStats = {};

        this.heatmapData.forEach(user => {
            let totalViewers = 0;
            let totalCount = 0;

            user.activity_heatmap.forEach(entry => {
                // Validate the date before converting it
                const timestamp = Date.parse(entry.TS);
                if (isNaN(timestamp)) {
                    console.error(`Invalid timestamp for user ${user.twitch_user_name}:`, entry.TS);
                    return; // Skip this entry
                }
                let date = new Date(timestamp); // Use the valid timestamp to create the Date object

                if (entry.CV !== null) {
                    totalViewers += entry.CV;
                    totalCount++;
                }

                let dateKey = date.toISOString().slice(0, 13);

                if (!processedEvents[dateKey]) {
                    processedEvents[dateKey] = [];
                }

                if (!processedEvents[dateKey].some(e => e.userName === user.twitch_user_name)) {
                    processedEvents[dateKey].push({
                        userName: user.twitch_user_name,
                        CV: entry.CV,
                    });
                }
            });
            if (totalCount > 0) {
                this.userViewerStats[user.twitch_user_name] = Math.round(totalViewers / totalCount);
            }
        });

        for (const [dateKey, users] of Object.entries(processedEvents)) {
            let numOfActiveUsers = users.length;
            let title = `Users: ${numOfActiveUsers}`;

            this.calendarOptions.events.push({
                title: title,
                start: new Date(`${dateKey}:00:00.000Z`),
                allDay: false,
                extendedProps: {
                    users: users
                }
            });
        }

        this.isLoading = false; // Set loading to false after processing is complete
    },
    handleDateClick(arg) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.changeView('timeGridDay', arg.date);
        this.calendarOptions.dateClick = this.handleTimeClick;
    },
    handleTimeClick(arg) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        // Removed the line that changes the view to month.

        const clickedEvent = this.calendarOptions.events.find(event => {
            const eventDateStr = new Date(event.start).toISOString().slice(0, 13);
            const clickedDateStr = new Date(arg.date).toISOString().slice(0, 13);
            return eventDateStr === clickedDateStr;
        });

        if (clickedEvent && clickedEvent.extendedProps.users) {
            this.activeUserNames = clickedEvent.extendedProps.users.map(u => ({
                userName: u.userName,
                avgViewers: this.userViewerStats[u.userName] || 'N/A',
            })).sort((a, b) => a.userName > b.userName ? 1 : -1); // Sort by userName

            this.showModal = true;
        }
    },
    handleEventClick(arg) {
        // Access extended properties of the clicked event
        const clickedEvent = arg.event.extendedProps;

        if (clickedEvent && clickedEvent.users) {
            this.activeUserNames = clickedEvent.users.map(u => ({
                userName: u.userName,
                avgViewers: this.userViewerStats[u.userName] || 'N/A',
            })).sort((a, b) => a.userName > b.userName ? 1 : -1);  // Sort by userName

            this.showModal = true;
        }
    },
    applyAllFilters() {
        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.removeAllEvents();
        
        let filteredEvents = [...this.calendarOptions.events];

        // Filter by Day of Week
        if (this.filterDayOfWeek) {
            const dayIndex = this.daysOfWeek.indexOf(this.filterDayOfWeek);
            filteredEvents = filteredEvents.filter(event => {
                const eventDate = new Date(event.start);
                return eventDate.getDay() === dayIndex + 1; // JavaScript getDay() is 0-indexed
            });
        }

        // Filter by Hour of Day
        if (this.filterHourOfDay !== "") {
            const selectedHour = parseInt(this.filterHourOfDay.split(' ')[0], 10); // Considering the format "HH AM/PM"
            const amPm = this.filterHourOfDay.split(' ')[1];
            const hourIn24 = amPm === 'AM' ? selectedHour : selectedHour + 12;
            filteredEvents = filteredEvents.filter(event => {
                const eventDate = new Date(event.start);
                return eventDate.getHours() === hourIn24;
            });
        }

        // Filter by Date Range
        if (this.selectedStartDate && this.selectedEndDate) {
            const startDate = new Date(this.selectedStartDate);
            const endDate = new Date(this.selectedEndDate);
            endDate.setHours(23, 59, 59); // To include the events on the end date

            filteredEvents = filteredEvents.filter(event => {
                const eventDate = new Date(event.start);
                return eventDate >= startDate && eventDate <= endDate;
            });
        }

        // Filter by Twitch Username
        if (this.filterTwitchUserID) {
            filteredEvents = filteredEvents.filter(event => {
                if (event.extendedProps && Array.isArray(event.extendedProps.users)) {
                    return event.extendedProps.users.some(user => 
                        user && user.userName 
                        && user.userName.toLowerCase().includes(this.filterTwitchUserID.toLowerCase())
                    );
                }
                return false;
            });
        }

        calendarApi.addEventSource(filteredEvents);
    },

    filterByDayOfWeek() {
        this.applyAllFilters();
    },

    filterByHourOfDay() {
        this.applyAllFilters();
    },

    filterByDateRange() {
        this.applyAllFilters();
    },

    filterEvents() {
        this.applyAllFilters();
    },
    closeModal() {
        this.showModal = false;
        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.changeView('dayGridMonth');
        this.calendarOptions.dateClick = this.handleDateClick;
    },
    resetFilters() {
        this.filterTwitchUserID = "";
        this.filterDayOfWeek = "";
        this.filterHourOfDay = "";
        this.selectedStartDate = null;
        this.selectedEndDate = null; 
        datepicker.clear();
        this.applyAllFilters();
    }
  },
};
</script>
<style scoped>
.modal {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  width: calc(100% - 100px); /* taking 100% width of parent minus some margin */
  height: calc(100% - 100px); /* taking 100% height of parent minus some margin */
  overflow-y: auto; /* making it scrollable */
  position: relative; /* To position the close button absolutely */
}

.close {
  cursor: pointer;
  position: absolute; /* To make it stay at the top-right corner */
  top: 10px;
  right: 10px;
}
.user-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); /* Set minimum width to 200px */
  gap: 16px;
  align-content: flex-start;
  height: 80vh;
  overflow-y: auto; /* If you still want vertical scroll for the entire grid */
}
.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8); /* Semi-transparent white */
  z-index: 1000; /* Ensure it's above other content */
}

.loading-message {
  padding: 20px;
  background-color: #000;
  color: #fff;
  border-radius: 5px;
}

</style>
